import Vue from 'vue';
import UiButton from '@omnica/button-vue2';
import UiInput from '@omnica/input-vue2';
import { vTooltip } from '@omnica/tooltip-vue2';
import { uid, isNumeric, DIRECTION, i18n, normalize, VIOLATION } from '@omnica/input-number';
import { ALIGN } from '@omnica/error';
import { APPEARANCE, SIZE } from '@omnica/input';
import { PLACEMENT } from '@omnica/popper';

var OmnicaIconAdd = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M19 11.5v1a.5.5 0 01-.5.5H13v5.5a.5.5 0 01-.5.5h-1a.5.5 0 01-.5-.5V13H5.5a.5.5 0 01-.5-.5v-1a.5.5 0 01.5-.5H11V5.5a.5.5 0 01.5-.5h1a.5.5 0 01.5.5V11h5.5a.5.5 0 01.5.5z","fill":"currentColor"}})]) } };

var OmnicaIconCaretDown = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M8.138 11.06A.518.518 0 018 10.705c0-.133.05-.261.138-.355l.187-.2A.442.442 0 018.66 10h6.68a.443.443 0 01.335.15l.187.2a.519.519 0 01.138.355c0 .133-.05.26-.138.355l-3.536 3.788A.434.434 0 0112 15a.434.434 0 01-.326-.152L8.138 11.06z","fill":"currentColor"}})]) } };

var OmnicaIconCaretUp = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M16.14 12.94a.5.5 0 010 .71l-.2.2a.49.49 0 01-.36.15H8.42a.49.49 0 01-.36-.15l-.2-.2a.5.5 0 010-.71l3.79-3.79a.48.48 0 01.7 0l3.79 3.79z","fill":"currentColor"}})]) } };

var OmnicaIconRemove = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 24 24","xmlns":"http://www.w3.org/2000/svg"}},[_c('path',{attrs:{"d":"M5.5 13.25c-.276 0-.5-.28-.5-.625v-1.25c0-.345.224-.625.5-.625h13c.276 0 .5.28.5.625v1.25c0 .345-.224.625-.5.625h-13z","fill":"currentColor"}})]) } };

var script = Vue.extend({
    name: 'UiInputNumber',
    components: {
        OmnicaIconAdd: OmnicaIconAdd,
        OmnicaIconCaretDown: OmnicaIconCaretDown,
        OmnicaIconCaretUp: OmnicaIconCaretUp,
        OmnicaIconRemove: OmnicaIconRemove,
        UiButton: UiButton,
        UiInput: UiInput,
    },
    directives: {
        tooltip: vTooltip,
    },
    model: {
        prop: 'value',
        event: 'update:value',
    },
    props: {
        /** Атрибут id нативного поля ввода input. Должен быть уникальным на странице */
        id: {
            type: String,
            default: function () { return uid(); },
        },
        /** Атрибут name нативного поля ввода input */
        name: {
            type: null,
            validator: function (name) { return typeof name === 'string' || name === null; },
            default: null,
        },
        /** Значение поля ввода */
        value: {
            type: [Number, String],
            default: 0,
        },
        /** Максимально допустимое значение. Атрибут max нативного поля ввода type="number" */
        max: {
            type: [Number, String],
            validator: isNumeric,
            default: Infinity,
        },
        /** Минимальное допустимое значение. Атрибут min нативного поля ввода type="number" */
        min: {
            type: [Number, String],
            validator: isNumeric,
            default: -Infinity,
        },
        /** Шаг изменения значения */
        step: {
            type: [Number, String],
            validator: isNumeric,
            default: 1,
        },
        /** Ввод только целых значений */
        integer: {
            type: Boolean,
            default: false,
        },
        /** Максимальное количество знаков после запятой */
        decimalPlaces: {
            type: Number,
            default: null,
        },
        /** Отключает возможность оставить поле пустым */
        strict: {
            type: Boolean,
            default: false,
        },
        /** Позволять или нет ввод значений, выходящих за пределы [min, max] */
        strictLimits: {
            type: Boolean,
            default: true,
        },
        /** Блокировать или нет кнопки увеличения/уменьшения при достижении максимума/минимуму */
        strictStepping: {
            type: Boolean,
            default: true,
        },
        /** Массив ошибок */
        errors: {
            type: Array,
            default: function () { return []; },
        },
        /** Определяет, по какому краю будет выровнен текст ошибки */
        errorsTextAlign: {
            type: String,
            validator: function (align) { return Object.values(ALIGN).includes(align); },
            default: ALIGN.LEFT,
        },
        /** Класс всплывающей подсказки с ошибкой */
        errorsTooltipClass: {
            type: String,
            default: '',
        },
        /** Контейнер всплывающей подсказки с ошибкой */
        errorsTooltipContainer: {
            type: null,
            validator: function (value) { return value === null || value instanceof Element || typeof value === 'string'; },
            default: null,
        },
        /** Позиционирование всплывающей подсказки с ошибкой */
        errorsTooltipPlacement: {
            type: String,
            default: PLACEMENT.BOTTOM,
        },
        /** Добавляет иконку сброса в виде крестика */
        clearable: {
            type: Boolean,
            default: false,
        },
        /** Отключает поле ввода */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Делает поле ввода доступным только для чтения */
        readonly: {
            type: Boolean,
            default: false,
        },
        /** Внешний вид */
        appearance: {
            type: String,
            validator: function (appearance) { return Object.values(APPEARANCE).includes(appearance); },
            default: APPEARANCE.OUTLINED,
        },
        /** Направление кнопок */
        direction: {
            type: String,
            validator: function (direction) { return Object.values(DIRECTION).includes(direction); },
            default: DIRECTION.HORIZONTAL,
        },
        responsive: {
            type: Boolean,
            default: false,
        },
        /** Размер поля ввода */
        size: {
            type: String,
            validator: function (size) { return Object.values(SIZE).includes(size); },
            default: SIZE.SM,
        },
        /** Преобразователь всплывающей подсказки у кнопок уменьшения / увеличения значения */
        buttonTooltip: {
            type: Function,
            default: function (options) { return options; },
        },
        /** Опции для переопределения текстов в компоненте, переводимых с помощью компонента @omnica/i18n */
        i18nOptions: {
            type: null,
            validator: function (options) { return typeof options === 'object' || options === null; },
            default: null,
        },
        /** Локаль перевода текста */
        locale: {
            type: String,
            default: i18n.fallback,
        },
    },
    data: function () {
        return {
            model: normalize(this.value, this.strictLimits ? [Number(this.min), Number(this.max)] : [-Infinity, Infinity]),
            violations: [],
        };
    },
    computed: {
        APPEARANCE: function () { return APPEARANCE; },
        DIRECTION: function () { return DIRECTION; },
        SIZE: function () { return SIZE; },
        VIOLATION: function () { return VIOLATION; },
        canDecrease: function () {
            return !this.strictStepping || !this.disabled && !this.readonly && this.model > this.range.min;
        },
        canIncrease: function () {
            return !this.strictStepping || !this.disabled && !this.readonly && this.model < this.range.max;
        },
        i18n: function () {
            var _a, _b;
            return i18n.init((_b = (_a = this.$omnicaI18n) === null || _a === void 0 ? void 0 : _a.locale) !== null && _b !== void 0 ? _b : this.locale, this.i18nOptions);
        },
        range: function () {
            var max = Number(this.max);
            var min = Number(this.min);
            return { max: max, min: min, toArray: function () { return [min, max]; } };
        },
    },
    watch: {
        value: {
            immediate: true,
            handler: function (value) {
                if (Number(value) !== Number(this.model)) {
                    this.model = normalize(value, [-Infinity, Infinity]);
                    this.validate();
                }
            },
        },
        max: {
            immediate: true,
            handler: function () {
                this.validate();
            },
        },
        min: {
            immediate: true,
            handler: function () {
                this.validate();
            },
        },
    },
    methods: {
        update: function (raw) {
            if (raw === void 0) { raw = false; }
            this.model = !isNumeric(this.model) ? 0 : this.model;
            var normalized = +this.model;
            var value = !this.strict && this.model === ''
                ? this.model
                : this.strictLimits && !raw
                    ? Math.max(Math.min(this.range.max, normalized), this.range.min)
                    : normalized;
            this.$emit('change', value);
            this.$emit('update:value', value);
        },
        onInput: function (value) {
            this.model = value;
            this.violations = [];
            this.update(true);
        },
        onChange: function (value) {
            var limits = this.strictLimits ? this.range.toArray() : [-Infinity, Infinity];
            this.model = normalize(value, limits);
            this.violations = [];
            this.update();
        },
        onFocus: function (event) {
            this.violations = [];
            this.$emit('focus', event);
        },
        onBlur: function (event) {
            this.validate();
            this.update();
            this.$emit('blur', event);
        },
        validate: function () {
            if (!isNumeric(this.model)) {
                this.violations.push(VIOLATION.NAN);
            }
            if (this.model > this.range.max) {
                this.violations.push(VIOLATION.TOO_LARGE);
            }
            if (this.model < this.range.min) {
                this.violations.push(VIOLATION.TOO_SMALL);
            }
        },
        decrease: function () {
            var _a;
            if (this.canDecrease) {
                var value = (+this.model - Number(this.step)).toFixed((_a = this.decimalPlaces) !== null && _a !== void 0 ? _a : 1);
                this.model = normalize(value, this.range.toArray());
                this.violations = [];
                this.$emit('change', this.model);
                this.$emit('update:value', this.model);
                this.$emit('decrease', this.model);
            }
        },
        increase: function () {
            var _a;
            if (this.canIncrease) {
                var value = (+this.model + Number(this.step)).toFixed((_a = this.decimalPlaces) !== null && _a !== void 0 ? _a : 1);
                this.model = normalize(value, this.range.toArray());
                this.violations = [];
                this.$emit('change', this.model);
                this.$emit('update:value', this.model);
                this.$emit('increase', this.model);
            }
        },
        toButtonTooltip: function (options, action) {
            var process = this.buttonTooltip;
            return process(options, action);
        },
    },
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _obj;
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    _vm._g(
      _vm._b(
        {
          class:
            ((_obj = {}),
            (_obj["omnica-input-number"] = true),
            (_obj["omnica-input-number_" + _vm.appearance] = true),
            (_obj["omnica-input-number_" + _vm.direction] = true),
            (_obj["omnica-input-number_" + _vm.size] = true),
            (_obj["omnica-input-number_clearable"] = _vm.clearable),
            (_obj["omnica-input-number_disabled"] = _vm.disabled),
            (_obj["omnica-input-number_readonly"] = _vm.readonly),
            (_obj["omnica-input-number_responsive"] = _vm.responsive),
            _obj),
          attrs: { type: "grid" },
        },
        "div",
        _vm.$attrs,
        false
      ),
      _vm.$listeners
    ),
    [
      _vm.direction === _vm.DIRECTION.HORIZONTAL
        ? _c(
            "UiButton",
            {
              directives: [
                {
                  name: "tooltip",
                  rawName: "v-tooltip",
                  value: _vm.toButtonTooltip(
                    {
                      content: _vm.i18n.t("canNotDecrease"),
                      centered: true,
                      disabled: _vm.canDecrease,
                    },
                    "decrease"
                  ),
                  expression:
                    "toButtonTooltip({\n            content: i18n.t('canNotDecrease'),\n            centered: true,\n            disabled: canDecrease,\n        }, 'decrease')",
                },
              ],
              staticClass: "omnica-input-number__button omnica-button_muted",
              attrs: {
                id: _vm.id + "-decrease",
                "aria-label": _vm.i18n.t("decreaseBy", { step: _vm.step }),
                size:
                  _vm.appearance === _vm.APPEARANCE.INLINE &&
                  [_vm.SIZE.XS, _vm.SIZE.SM].includes(_vm.size)
                    ? "xs"
                    : "sm",
                disabled: !_vm.canDecrease,
                appearance: "tertiary",
                shape: "square",
              },
              on: { click: _vm.decrease },
            },
            [_c("OmnicaIconRemove", { attrs: { "aria-hidden": "true" } })],
            1
          )
        : _c(
            "button",
            {
              directives: [
                {
                  name: "tooltip",
                  rawName: "v-tooltip",
                  value: _vm.toButtonTooltip(
                    {
                      content: _vm.i18n.t("canNotDecrease"),
                      centered: true,
                      disabled: _vm.canDecrease,
                    },
                    "decrease"
                  ),
                  expression:
                    "toButtonTooltip({\n            content: i18n.t('canNotDecrease'),\n            centered: true,\n            disabled: canDecrease,\n        }, 'decrease')",
                },
              ],
              staticClass: "omnica-input-number__button",
              attrs: {
                id: _vm.id + "-decrease",
                "aria-label": _vm.i18n.t("decreaseBy", { step: _vm.step }),
                disabled: !_vm.canDecrease,
                type: "button",
              },
              on: { click: _vm.decrease },
            },
            [
              _c("OmnicaIconCaretDown", {
                staticClass: "omnica-input-number__icon",
                attrs: { "aria-hidden": "true" },
              }),
            ],
            1
          ),
      _vm._v(" "),
      _c("UiInput", {
        staticClass: "omnica-input-number__input",
        attrs: {
          id: _vm.id,
          name: _vm.name,
          value: _vm.model,
          max: _vm.strictLimits ? _vm.range.max : Infinity,
          min: _vm.strictLimits ? _vm.range.min : -Infinity,
          integer: _vm.integer,
          "max-decimal-places": _vm.decimalPlaces,
          errors: _vm.errors.concat(
            _vm.violations.map(function (v) {
              switch (v) {
                case _vm.VIOLATION.NAN:
                  return _vm.i18n.t("violation.nan")
                case _vm.VIOLATION.TOO_LARGE:
                  return _vm.i18n.t("violation.tooLarge")
                case _vm.VIOLATION.TOO_SMALL:
                  return _vm.i18n.t("violation.tooSmall")
              }
            })
          ),
          "errors-text-align": _vm.errorsTextAlign,
          "errors-tooltip-class": _vm.errorsTooltipClass,
          "errors-tooltip-container": _vm.errorsTooltipContainer,
          "errors-tooltip-placement": _vm.errorsTooltipPlacement,
          clearable: _vm.clearable,
          appearance: _vm.appearance,
          "fit-content": _vm.appearance === _vm.APPEARANCE.INLINE,
          "min-width": 22,
          size: _vm.size,
          disabled: _vm.disabled,
          readonly: _vm.readonly,
          type: "number",
          "disable-arrows": "",
          "errors-in-tooltip": "",
          "select-on-focus": "",
        },
        on: {
          input: _vm.onInput,
          change: _vm.onChange,
          focus: _vm.onFocus,
          blur: _vm.onBlur,
        },
        nativeOn: {
          keydown: [
            function ($event) {
              if (
                !$event.type.indexOf("key") &&
                _vm._k($event.keyCode, "up", 38, $event.key, ["Up", "ArrowUp"])
              ) {
                return null
              }
              $event.preventDefault();
              return _vm.increase.apply(null, arguments)
            },
            function ($event) {
              if (
                !$event.type.indexOf("key") &&
                _vm._k($event.keyCode, "down", 40, $event.key, [
                  "Down",
                  "ArrowDown",
                ])
              ) {
                return null
              }
              $event.preventDefault();
              return _vm.decrease.apply(null, arguments)
            },
          ],
        },
      }),
      _vm._v(" "),
      _vm.direction === _vm.DIRECTION.HORIZONTAL
        ? _c(
            "UiButton",
            {
              directives: [
                {
                  name: "tooltip",
                  rawName: "v-tooltip",
                  value: _vm.toButtonTooltip(
                    {
                      content: _vm.i18n.t("canNotIncrease"),
                      centered: true,
                      disabled: _vm.canIncrease,
                    },
                    "increase"
                  ),
                  expression:
                    "toButtonTooltip({\n            content: i18n.t('canNotIncrease'),\n            centered: true,\n            disabled: canIncrease,\n        }, 'increase')",
                },
              ],
              staticClass: "omnica-input-number__button omnica-button_muted",
              attrs: {
                id: _vm.id + "-increase",
                "aria-label": _vm.i18n.t("increaseBy", { step: _vm.step }),
                size:
                  _vm.appearance === _vm.APPEARANCE.INLINE &&
                  [_vm.SIZE.XS, _vm.SIZE.SM].includes(_vm.size)
                    ? "xs"
                    : "sm",
                disabled: !_vm.canIncrease,
                appearance: "tertiary",
                shape: "square",
              },
              on: { click: _vm.increase },
            },
            [_c("OmnicaIconAdd", { attrs: { "aria-hidden": "true" } })],
            1
          )
        : _c(
            "button",
            {
              directives: [
                {
                  name: "tooltip",
                  rawName: "v-tooltip",
                  value: _vm.toButtonTooltip(
                    {
                      content: _vm.i18n.t("canNotIncrease"),
                      centered: true,
                      disabled: _vm.canIncrease,
                    },
                    "increase"
                  ),
                  expression:
                    "toButtonTooltip({\n            content: i18n.t('canNotIncrease'),\n            centered: true,\n            disabled: canIncrease,\n        }, 'increase')",
                },
              ],
              staticClass: "omnica-input-number__button",
              attrs: {
                id: _vm.id + "-increase",
                "aria-label": _vm.i18n.t("increaseBy", { step: _vm.step }),
                disabled: !_vm.canIncrease,
                type: "button",
              },
              on: { click: _vm.increase },
            },
            [
              _c("OmnicaIconCaretUp", {
                staticClass: "omnica-input-number__icon",
                attrs: { "aria-hidden": "true" },
              }),
            ],
            1
          ),
    ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export { __vue_component__ as default };
